/**
 * Orders Page
 */
import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getCustomCtaText from '@/utils/getCustomCtaText';
import { AuthenticateUserModal } from './components/AuthenticateUserModal';
import { Header } from './components/Headers';
import { OrderHeaderView } from './components/OrderHeaderView';
import { NoItemView } from './components/NoItemView';
import { ListBody } from './components/ListBody';
import { ORDERS_LIST_TYPE } from '@/constants/order';
import { OrderContext } from './context';
import { getAuthToken } from 'src/utils/getAuthToken';
import { fetchOrderList } from 'src/redux/actions';
import { GlobalLoader } from 'src/components/WidgetMaker/WidgetDnD/globalLoader';
import { getThemeColor } from 'src/utils/getThemeColor';

export const INITIAL_PAGINATION_CONFIG = {
  pageNum: 1,
  isNext: true,
};

const Orders = () => {
  const dispatch = useDispatch();

  const { userData, storeId } = useSelector((state) => ({
    userData: state.userReducer.data,
    storeId: state.storeReducer.store?.store_id,
  }));

  const [orderList, setOrderList] = useState({
    [ORDERS_LIST_TYPE.COMPLETED]: [],
    [ORDERS_LIST_TYPE.PENDING]: [],
    [ORDERS_LIST_TYPE.FAILED_PAYMENTS]: [],
  });

  const [pagination, setPagination] = useState({
    [ORDERS_LIST_TYPE.COMPLETED]: INITIAL_PAGINATION_CONFIG,
    [ORDERS_LIST_TYPE.PENDING]: INITIAL_PAGINATION_CONFIG,
    [ORDERS_LIST_TYPE.FAILED_PAYMENTS]: INITIAL_PAGINATION_CONFIG,
  });

  const [isLoading, setIsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(ORDERS_LIST_TYPE.PENDING);

  useEffect(() => {
    const currPagination = pagination[activeTab];
    if (currPagination.pageNum === 1 && currPagination.isNext) {
      loadMoreData();
    }
  }, [activeTab, pagination]);

  function resetActiveTabOrdersAndPagination() {
    setOrderList({
      ...orderList,
      [activeTab]: [],
    });
    setPagination({
      ...pagination,
      [activeTab]: INITIAL_PAGINATION_CONFIG,
    });
  }

  function setActiveTabPagination(data) {
    setPagination({
      ...pagination,
      [activeTab]: {
        ...pagination[activeTab],
        ...data,
      },
    });
  }

  function setActiveTabOrders(data) {
    setOrderList({
      ...orderList,
      [activeTab]: [...orderList[activeTab], ...data],
    });
  }

  const loadMoreData = async () => {
    const authToken = getAuthToken();

    if (!pagination[activeTab].isNext || !authToken) {
      return;
    }
    setIsLoading(true);
    const payload = {
      phone: userData.phone || '',
      storeid: storeId,
      pageNum: pagination[activeTab].pageNum,
      orderStatus: activeTab,
    };

    if (!payload.phone) {
      payload.userId = userData.user_id || '';
    }

    dispatch(
      fetchOrderList(payload, (status, data = {}) => {
        const { orders = [], isNextPage = false } = data;
        setIsLoading(false);
        if (status) {
          setActiveTabOrders(orders);
          setActiveTabPagination({
            pageNum: pagination[activeTab].pageNum + 1,
            isNext: isNextPage,
          });
        } else {
          setActiveTabPagination({
            isNext: false,
          });
        }
      })
    );
  };

  const onChangeActiveTab = (tabType) => {
    setActiveTab(tabType);
  };

  const contextValue = {
    activeTab,
    onChangeActiveTab,
    isLoading,
    setIsLoading,
    loadMoreData,
    orders: orderList[activeTab],
    pagination: pagination[activeTab],
    resetActiveTabOrdersAndPagination,
  };

  return (
    <OrderContext.Provider value={contextValue}>
      <section className="premium-order-list-screen">
        <Header />
        <section className="premium-order-list-wrapper !tw-pb-[40px]">
          <div className="premium-order-heading">{getCustomCtaText('my_orders_cta')}</div>
          <OrderHeaderView />
          {orderList[activeTab].length ? <ListBody /> : !isLoading && <NoItemView />}
          {isLoading && (
            <div className="tw-flex tw-w-full tw-justify-center tw-pt-[20px] md:tw-pt-[40px]">
              <GlobalLoader type="loader1" color={getThemeColor()} />
            </div>
          )}
        </section>
        <AuthenticateUserModal />
      </section>
    </OrderContext.Provider>
  );
};

export default memo(Orders);
